import {
  ArrowLeftStroke,
  CheckStroke,
  FileMultipleStroke,
  FloppyDisk1Stroke,
  Pencil1Stroke,
  PlusStroke,
  Trash3Stroke
} from '@lineiconshq/free-icons';
import { Lineicons, type LineiconsProps } from '@lineiconshq/react-lineicons';
import React, { useSyncExternalStore } from 'react';
import { AritoFormV2Context } from '@/components/custom/erp/form-v2/context';
import { isActionBusy, subscribeAction } from '@/lib/api-loading-store';
import { AritoIcon } from '@/components/custom/arito/icon';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type LineIconData = LineiconsProps['icon'];

const bottomBarIconMap: Partial<Record<number, LineIconData>> = {
  8: Trash3Stroke,
  9: Pencil1Stroke,
  11: FileMultipleStroke,
  571: PlusStroke,
  884: FloppyDisk1Stroke,
  885: ArrowLeftStroke
};

interface BottomBarActionButtonProps {
  onClick?: () => void;
  icon: number;
  title: string;
  primary?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
  className?: string;
}

const CLOSE_TITLES = new Set(['Đóng', 'Hủy', 'Close', 'Cancel']);

export const BottomBarActionButtonV2 = ({
  onClick,
  icon,
  title,
  primary = true,
  type,
  disabled,
  className: classNameProp
}: BottomBarActionButtonProps) => {
  const isCloseAction = CLOSE_TITLES.has(title);

  const handleClick = () => {
    if (type !== 'submit' && onClick) {
      onClick();
    }
  };

  const renderIcon = () => {
    if (!icon) return null;

    const lineIcon = title === 'Đồng ý' && icon === 884 ? CheckStroke : bottomBarIconMap[icon];

    return lineIcon ? (
      <span aria-hidden='true' className='flex size-6 shrink-0 items-center justify-center leading-none'>
        <Lineicons icon={lineIcon} className='block' size={20} strokeWidth={1.8} />
      </span>
    ) : (
      <span aria-hidden='true' className='flex size-6 shrink-0 items-center justify-center leading-none'>
        <AritoIcon icon={icon} className='text-[20px] leading-none' />
      </span>
    );
  };

  const buttonVariant = isCloseAction ? 'destructive' : primary ? 'default' : 'secondary';

  return (
    <Button
      variant={buttonVariant}
      className={cn(
        'h-[var(--erp-command-button-height)] min-w-[100px] gap-2 rounded-[3px] px-4 text-[15px] font-semibold leading-none [&_svg]:size-auto',
        primary && !isCloseAction && 'bg-main text-main-foreground hover:bg-main/80 disabled:bg-gray-400',
        classNameProp
      )}
      onClick={handleClick}
      type={type || 'button'}
      title={title}
      disabled={disabled}
    >
      {renderIcon()}
      <span className='leading-none'>{title}</span>
    </Button>
  );
};

export interface BottomActionBarV2Props {
  mode: 'view' | 'edit' | 'add' | 'search';
  bottomBar?: React.ReactNode;
  onSubmit?: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  onClose: () => void;
  className?: string;
  loading?: boolean;
  submitTitle?: string;
}

export const BottomActionBarV2 = ({
  mode,
  bottomBar,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  onClose,
  className,
  loading: loadingProp,
  submitTitle = 'Lưu'
}: BottomActionBarV2Props) => {
  const { loading } = useAuth();
  const formContext = React.useContext(AritoFormV2Context);
  const apiActionLoading = useSyncExternalStore(subscribeAction, isActionBusy, isActionBusy);
  const isLoading = Boolean(loadingProp || loading || formContext?.loading || apiActionLoading);
  const viewModeButtons = [
    { show: onAdd, onClick: onAdd, icon: 571, title: 'Thêm', primary: true },
    { show: onEdit, onClick: onEdit, icon: 9, title: 'Sửa' },
    { show: onDelete, onClick: onDelete, icon: 8, title: 'Xoá' },
    { show: onCopy, onClick: onCopy, icon: 11, title: 'Sao chép' }
  ];

  return (
    <div
      className={cn(
        `z-10 flex w-full items-center justify-end gap-1 border-t border-gray-200 bg-[#f9fcfd] px-4 py-2 sm:px-4 sm:py-2`,
        className
      )}
    >
      {bottomBar || (
        <>
          {isLoading && (
            <div className='mr-auto flex items-center justify-center'>
              <div className='size-8 animate-spin rounded-full border-b-[3px] border-t-[3px] border-main'></div>
            </div>
          )}
          {mode !== 'view' && (
            <BottomBarActionButtonV2
              onClick={onSubmit}
              icon={884}
              title={submitTitle}
              primary
              type='submit'
              disabled={isLoading}
            />
          )}
          {mode === 'view' && (
            <>
              {viewModeButtons.map(
                button =>
                  button.show && (
                    <BottomBarActionButtonV2
                      key={button.title}
                      onClick={button.onClick!}
                      icon={button.icon}
                      title={button.title}
                      primary={button.primary === true}
                      disabled={isLoading}
                    />
                  )
              )}
            </>
          )}
          <BottomBarActionButtonV2 onClick={onClose} icon={885} primary={false} title='Hủy' disabled={isLoading} />
        </>
      )}
    </div>
  );
};

export default BottomActionBarV2;
